/**
 * AssignDealTagsDialog - Dialog para atribuir tags a um negócio
 * 
 * Versão: 1.0.0
 * - Lista de checkboxes com tags disponíveis
 * - Salva alterações atomicamente
 * - Botão para gerenciar tags
 * 
 * RELEASE: 2026-01-07
 */

import React, { useState, useEffect } from 'react';
import { Settings, Loader2, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useDealTags,
  useDealTagAssignments,
  useAssignDealTags,
} from '@/hooks/useDealTags';
import { DealTagBadge } from './DealTagBadge';
import { DealTagManagementDialog } from './DealTagManagementDialog';
import { usePermissions } from '@/hooks/usePermissions';

// Props do componente
interface AssignDealTagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  tenantId: string;
}

export function AssignDealTagsDialog({
  open,
  onOpenChange,
  dealId,
  tenantId,
}: AssignDealTagsDialogProps) {
  // Estados
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [managementOpen, setManagementOpen] = useState(false);

  // Permissões granulares para Tags de Negócio (CRM)
  const { canView, canCreate, canEdit, canDelete } = usePermissions();
  const canViewTags = canView('crm_deal_tags');
  const canCreateTags = canCreate('crm_deal_tags');
  const canEditTags = canEdit('crm_deal_tags');
  const canDeleteTags = canDelete('crm_deal_tags');

  // Queries - lazy: só busca quando dialog está aberto (evita N+1 no Kanban)
  const { data: allTags = [], isLoading: loadingTags } = useDealTags(open ? tenantId : undefined);
  const { data: assignments = [], isLoading: loadingAssignments } = useDealTagAssignments(open ? dealId : undefined);
  const assignMutation = useAssignDealTags();
  
  // Sincronizar seleção quando abre ou dados mudam
  useEffect(() => {
    if (open && assignments.length >= 0) {
      setSelectedTagIds(assignments.map(a => a.tag_id));
    }
  }, [open, assignments]);
  
  // Handlers
  const toggleTag = (tagId: string) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };
  
  const handleSave = async () => {
    await assignMutation.mutateAsync({
      dealId,
      tagIds: selectedTagIds,
      tenantId,
    });
    onOpenChange(false);
  };
  
  const isLoading = loadingTags || loadingAssignments;
  
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Selecionar Tags</DialogTitle>
              {/* Botão de gerenciar tags só aparece se o usuário tiver permissão de visualizar */}
              {canViewTags && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setManagementOpen(true)}
                  title="Gerenciar tags"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              )}
            </div>
            <DialogDescription>
              Selecione as tags para este negócio.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[250px] pr-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : allTags.length === 0 ? (
              <div className="text-center py-8 space-y-3">
                <p className="text-muted-foreground text-sm">
                  Nenhuma tag disponível.
                </p>
                {/* Botão "Criar tags" exibido somente quando o usuário pode criar */}
                {canCreateTags && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setManagementOpen(true)}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Criar tags
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {allTags.map((tag) => {
                  const isSelected = selectedTagIds.includes(tag.id);
                  
                  return (
                    <label
                      key={tag.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-muted'
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleTag(tag.id)}
                      />
                      <DealTagBadge name={tag.name} color={tag.color} size="md" />
                      {isSelected && (
                        <Check className="h-4 w-4 ml-auto text-primary" />
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={assignMutation.isPending}
            >
              {assignMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog de gerenciamento */}
      <DealTagManagementDialog
        open={managementOpen}
        onOpenChange={setManagementOpen}
        tenantId={tenantId}
        canCreate={canCreateTags}
        canEdit={canEditTags}
        canDelete={canDeleteTags}
      />
    </>
  );
}
